/**
 * Organism: OnboardingTour (v2 — Sage-led conversational flow)
 *
 * Role-based paths:
 *   Owner/Admin → 3-step creation flow (project → task → invite)
 *   PM/Foreman/other → 3-step orientation flow (tasks nav → status → teamchat)
 *
 * Fires activity_log events: onboarding_started, onboarding_step_completed (1-3),
 *   onboarding_completed, onboarding_skipped.
 *
 * Mobile-responsive: full-screen panel at <640px.
 * Restart: handled externally via PATCH /api/v1/auth/tour { reset: true }
 */
import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { authedFetch } from '@/lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────

interface OnboardingTourProps {
  onComplete: () => void
  onDismiss: () => void
  userName?: string
  userRole?: string
  companyName?: string
}

type Path = 'owner' | 'member'

interface StepState {
  projectName: string
  projectType: string
  taskName: string
  inviteEmail: string
  projectCreated: boolean
  taskCreated: boolean
  inviteSent: boolean
  projectId?: number
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function authFetch(path: string, opts: RequestInit = {}) {
  return authedFetch('/api/v1' + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers as Record<string, string> || {}),
    },
  }, { redirectOnAuthFailure: false })
}

function fireEvent(event: string, step?: number) {
  authFetch('/onboarding/event', {
    method: 'POST',
    body: JSON.stringify({ event, step: step || 0 }),
  }).catch(() => {})
}

function isOwnerRole(role?: string): boolean {
  const r = (role || '').toLowerCase()
  return r === 'owner' || r === 'admin' || r === 'account_owner'
}

const PROJECT_TYPES = [
  'Residential New Build', 'Residential Renovation', 'Commercial New Build',
  'Commercial Renovation', 'Industrial', 'Infrastructure', 'Landscaping',
  'Interior Fit-Out', 'Other',
]

// ── Step Progress Indicator ────────────────────────────────────────────────────

function StepProgress({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
      {Array.from({ length: total }, (_, i) => {
        const num = i + 1
        const done = num < current
        const active = num === current
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
              background: done ? 'var(--st-success)' : active ? 'var(--brand-gold)' : 'var(--bg-elevated)',
              color: done ? 'var(--text-inverse)' : active ? 'var(--brand-gold-text)' : 'var(--text-muted)',
              border: `2px solid ${done ? 'var(--st-success)' : active ? 'var(--brand-gold)' : 'var(--border)'}`,
              transition: 'all 0.2s',
              flexShrink: 0,
            }}>
              {done ? '✓' : num}
            </div>
            {i < total - 1 && (
              <div style={{
                width: 24, height: 2, borderRadius: 1,
                background: done ? 'var(--st-success)' : 'var(--border)',
              }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Sage Bubble ───────────────────────────────────────────────────────────────

function SageBubble({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: 'var(--brand-gold)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 13, color: 'var(--brand-gold-text)', fontFamily: 'inherit',
      }}>
        S
      </div>
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: '4px 12px 12px 12px',
        padding: '10px 14px',
        fontSize: 14,
        color: 'var(--text-primary)',
        lineHeight: 1.55,
        flex: 1,
      }}>
        {children}
      </div>
    </div>
  )
}

// ── Mini Input ────────────────────────────────────────────────────────────────

function MiniInput({ label, value, onChange, placeholder, type = 'text', autoFocus = false }: {
  label?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  autoFocus?: boolean
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</label>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 12px', fontSize: 13, borderRadius: 8,
          border: '1px solid var(--border)', background: 'var(--bg-elevated)',
          color: 'var(--text-primary)', fontFamily: 'inherit',
          outline: 'none',
        }}
        onFocus={e => e.currentTarget.style.borderColor = 'var(--brand-gold)'}
        onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
      />
    </div>
  )
}

function MiniSelect({ label, value, onChange, options }: {
  label?: string
  value: string
  onChange: (v: string) => void
  options: string[]
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</label>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 12px', fontSize: 13, borderRadius: 8,
          border: '1px solid var(--border)', background: 'var(--bg-elevated)',
          color: 'var(--text-primary)', fontFamily: 'inherit', cursor: 'pointer',
        }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

// ── CTAs ──────────────────────────────────────────────────────────────────────

function PrimaryBtn({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? 'var(--bg-elevated)' : 'var(--brand-gold)',
        color: 'var(--brand-gold-text)', border: 'none', borderRadius: 8,
        padding: '10px 18px', fontSize: 13, fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        opacity: disabled ? 0.5 : 1,
        transition: 'filter 0.15s',
      }}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.08)' }}
      onMouseLeave={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.filter = '' }}
    >
      {children}
    </button>
  )
}

function SkipBtn({ onClick, label }: { onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 12, color: 'var(--text-muted)', fontFamily: 'inherit',
        padding: '10px 0',
        textDecoration: 'underline',
      }}
      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
    >
      {label || 'Skip this step →'}
    </button>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function OnboardingTour({ onComplete, onDismiss, userName, userRole, companyName }: OnboardingTourProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState(0) // 0=greeting, 1,2,3=steps, 4=aha
  const [path, setPath] = useState<Path | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<StepState>({
    projectName: '', projectType: 'Residential New Build',
    taskName: 'Review project scope', inviteEmail: '',
    projectCreated: false, taskCreated: false, inviteSent: false,
  })
  const startedRef = useRef(false)

  // Determine path from role
  useEffect(() => {
    const p: Path = isOwnerRole(userRole) ? 'owner' : 'member'
    setPath(p)
  }, [userRole])

  // Fire onboarding_started once
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true
      fireEvent('onboarding_started')
    }
  }, [])

  // Highlight nav element for member path
  useEffect(() => {
    if (path !== 'member' || step < 1 || step > 3) return
    const highlights = ['a[href="/tasks"]', null, null]
    const selector = highlights[step - 1]
    if (!selector) return
    const el = document.querySelector(selector)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      ;(el as HTMLElement).style.outline = '2px solid #EFF890'
      ;(el as HTMLElement).style.outlineOffset = '3px'
      return () => {
        ;(el as HTMLElement).style.outline = ''
        ;(el as HTMLElement).style.outlineOffset = ''
      }
    }
  }, [path, step])

  const advanceStep = (nextStep: number) => {
    if (nextStep > step && step >= 1 && step <= 3) {
      fireEvent('onboarding_step_completed', step)
    }
    setError(null)
    setStep(nextStep)
  }

  const handleSkip = () => {
    fireEvent('onboarding_skipped')
    onDismiss()
  }

  const handleComplete = () => {
    fireEvent('onboarding_completed')
    onComplete()
  }

  // Owner Step 1: Create project
  const handleCreateProject = async () => {
    if (!state.projectName.trim()) { setError(t('onboarding.projectNameRequired', 'Project name is required')); return }
    setLoading(true); setError(null)
    try {
      const res = await authFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({ name: state.projectName, project_type: state.projectType, status: 'active' }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Could not create project')
      setState(s => ({ ...s, projectCreated: true, projectId: data.id }))
      setTimeout(() => advanceStep(2), 800)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // Owner Step 2: Create task
  const handleCreateTask = async () => {
    setLoading(true); setError(null)
    try {
      const body: Record<string, any> = { name: state.taskName || 'Review project scope', status: 'Not Started' }
      if (state.projectId) body.project_id = state.projectId
      const res = await authFetch('/tasks', { method: 'POST', body: JSON.stringify(body) })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Could not create task') }
      setState(s => ({ ...s, taskCreated: true }))
      setTimeout(() => advanceStep(3), 800)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // Owner Step 3: Invite
  const handleInvite = async () => {
    if (!state.inviteEmail.trim() || !state.inviteEmail.includes('@')) { advanceStep(4); return }
    setLoading(true); setError(null)
    try {
      const res = await authFetch('/onboarding/invite', {
        method: 'POST',
        body: JSON.stringify({ emails: [state.inviteEmail.trim()] }),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Could not send invite') }
      setState(s => ({ ...s, inviteSent: true }))
      setTimeout(() => advanceStep(4), 800)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const isMobile = window.innerWidth < 640

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--bg-surface)',
        display: 'flex', flexDirection: 'column',
        padding: 16, overflowY: 'auto',
      }
    : {
        position: 'fixed',
        bottom: 20, right: 20,
        zIndex: 9999,
        width: 380,
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        padding: 24,
      }

  if (!path) return null

  // ── Greeting ─────────────────────────────────────────────────────────────────
  if (step === 0) {
    const greeting = path === 'owner'
      ? t('onboarding.greetingOwner', "You're the account owner — let's get your first project set up.")
      : t('onboarding.greetingMember', `Welcome to {{company}}. Let's get you oriented — I'll show you where to find your work.`, { company: companyName || 'your company' })

    return (
      <div style={panelStyle} role="dialog" aria-modal="true" aria-label={t('onboarding.ariaLabel', 'Onboarding tour')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {t('onboarding.gettingStarted', 'Getting Started')}
          </span>
          <button onClick={handleSkip} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <SageBubble>
          {t('onboarding.hiName', `Hi {{name}} 👋`, { name: userName || 'there' })}<br /><br />
          {greeting}
        </SageBubble>

        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 20px' }}>
          {t('onboarding.takesSeconds', 'This takes about 60 seconds.')}
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SkipBtn onClick={handleSkip} label={t('onboarding.skipTour', 'Skip tour')} />
          <PrimaryBtn onClick={() => advanceStep(1)}>
            {t('onboarding.letsGo', "Let's go →")}
          </PrimaryBtn>
        </div>
      </div>
    )
  }

  // ── Aha moment ────────────────────────────────────────────────────────────────
  if (step === 4) {
    const summary: string[] = []
    if (state.projectCreated) summary.push(t('onboarding.ahaProject', `✅ Project created: {{name}}`, { name: state.projectName }))
    if (state.taskCreated) summary.push(t('onboarding.ahaTask', `✅ First task added: {{name}}`, { name: state.taskName || 'Review project scope' }))
    if (state.inviteSent) summary.push(t('onboarding.ahaInvite', `✅ Invite sent to: {{email}}`, { email: state.inviteEmail }))

    return (
      <div style={panelStyle} role="dialog" aria-modal="true">
        <SageBubble>
          <strong>{t('onboarding.ahaHeadline', "You're all set up! 🎉")}</strong><br /><br />
          {t('onboarding.ahaBody', "Here's what we just did together:")}
        </SageBubble>

        {summary.length > 0 && (
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            {summary.map((s, i) => (
              <div key={i} style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{s}</div>
            ))}
          </div>
        )}

        {path === 'member' && (
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
              {t('onboarding.memberAhaSummary', '✅ Your tasks are in the Tasks section\n✅ Update statuses with one tap\n✅ TeamChat keeps you connected')}
            </div>
          </div>
        )}

        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
          {t('onboarding.ahaCta', "Head to your dashboard to start working. I'll be here whenever you need me.")}
        </p>

        <PrimaryBtn onClick={handleComplete}>
          {t('onboarding.goToDashboard', 'Go to Dashboard →')}
        </PrimaryBtn>
      </div>
    )
  }

  // ── Owner path ────────────────────────────────────────────────────────────────
  if (path === 'owner') {
    return (
      <div style={panelStyle} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <StepProgress current={step} total={3} />
          <button onClick={handleSkip} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '0 0 0 8px', marginTop: -2 }}>×</button>
        </div>

        {/* Step 1: Create project */}
        {step === 1 && (
          <>
            <SageBubble>
              <strong>{t('onboarding.step1Headline', "Let's create your first project.")}</strong><br />
              {t('onboarding.step1Body', 'Every job in Tenon starts with a project. What are you working on?')}
            </SageBubble>
            {error && <div style={{ color: 'var(--st-error)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            {state.projectCreated
              ? <div style={{ color: 'var(--st-success)', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>✓ {t('onboarding.projectCreated', 'Project created!')}</div>
              : <>
                  <MiniInput
                    label={t('onboarding.projectName', 'Project name')}
                    value={state.projectName}
                    onChange={v => setState(s => ({ ...s, projectName: v }))}
                    placeholder={t('onboarding.projectPlaceholder', 'e.g. 42 Maple St — Renovation')}
                    autoFocus
                  />
                  <MiniSelect
                    label={t('onboarding.projectType', 'Project type')}
                    value={state.projectType}
                    onChange={v => setState(s => ({ ...s, projectType: v }))}
                    options={PROJECT_TYPES}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <SkipBtn onClick={() => advanceStep(2)} />
                    <PrimaryBtn onClick={handleCreateProject} disabled={loading}>
                      {loading ? t('onboarding.creating', 'Creating…') : t('onboarding.createProject', 'Create Project →')}
                    </PrimaryBtn>
                  </div>
                </>
            }
          </>
        )}

        {/* Step 2: Add task */}
        {step === 2 && (
          <>
            <SageBubble>
              <strong>{t('onboarding.step2Headline', 'Nice! Now add your first task.')}</strong><br />
              {t('onboarding.step2Body', "I've suggested a starting point — feel free to edit it.")}
            </SageBubble>
            {error && <div style={{ color: 'var(--st-error)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            {state.taskCreated
              ? <div style={{ color: 'var(--st-success)', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>✓ {t('onboarding.taskCreated', 'Task added!')}</div>
              : <>
                  <MiniInput
                    label={t('onboarding.taskName', 'Task name')}
                    value={state.taskName}
                    onChange={v => setState(s => ({ ...s, taskName: v }))}
                    placeholder={t('onboarding.taskPlaceholder', 'e.g. Review project scope')}
                    autoFocus
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <SkipBtn onClick={() => advanceStep(3)} />
                    <PrimaryBtn onClick={handleCreateTask} disabled={loading}>
                      {loading ? t('onboarding.adding', 'Adding…') : t('onboarding.addTask', 'Add Task →')}
                    </PrimaryBtn>
                  </div>
                </>
            }
          </>
        )}

        {/* Step 3: Invite */}
        {step === 3 && (
          <>
            <SageBubble>
              <strong>{t('onboarding.step3Headline', 'Almost done — want to invite a teammate?')}</strong><br />
              {t('onboarding.step3Body', 'Add one email now, or skip and do it from Settings later.')}
            </SageBubble>
            {error && <div style={{ color: 'var(--st-error)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            {state.inviteSent
              ? <div style={{ color: 'var(--st-success)', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>✓ {t('onboarding.inviteSent', 'Invite sent!')}</div>
              : <>
                  <MiniInput
                    label={t('onboarding.teamEmail', 'Team member email')}
                    value={state.inviteEmail}
                    onChange={v => setState(s => ({ ...s, inviteEmail: v }))}
                    placeholder="colleague@example.com"
                    type="email"
                    autoFocus
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <SkipBtn onClick={() => advanceStep(4)} label={t('onboarding.soloSkip', "I'm solo, skip this →")} />
                    <PrimaryBtn onClick={handleInvite} disabled={loading}>
                      {loading ? t('onboarding.sending', 'Sending…') : t('onboarding.sendInvite', 'Send Invite →')}
                    </PrimaryBtn>
                  </div>
                </>
            }
          </>
        )}
      </div>
    )
  }

  // ── Member path ────────────────────────────────────────────────────────────────
  if (path === 'member') {
    const memberSteps = [
      {
        sage: t('onboarding.memberStep1Sage', "Here's where your tasks live. 📋"),
        body: t('onboarding.memberStep1Body', 'The Tasks section shows everything assigned to you across all projects. Tap it in the sidebar anytime.'),
        cta: t('onboarding.memberStep1Cta', 'Got it →'),
        highlight: 'a[href="/tasks"]',
      },
      {
        sage: t('onboarding.memberStep2Sage', "Here's how to update a task status. ✏️"),
        body: t('onboarding.memberStep2Body', 'Open any task and tap the status badge to cycle it through Not Started → In Progress → Done. Your PM sees it update live.'),
        cta: t('onboarding.memberStep2Cta', 'Nice →'),
        highlight: null,
      },
      {
        sage: t('onboarding.memberStep3Sage', "And this is TeamChat — your crew communicates here. 💬"),
        body: t('onboarding.memberStep3Body', 'Team messages and project updates all in one place. Tap the chat button (bottom-right) to open it.'),
        cta: t('onboarding.memberStep3Cta', "I'm ready →"),
        highlight: null,
      },
    ]

    const ms = memberSteps[step - 1]
    if (!ms) return null

    return (
      <div style={panelStyle} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <StepProgress current={step} total={3} />
          <button onClick={handleSkip} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '0 0 0 8px', marginTop: -2 }}>×</button>
        </div>

        <SageBubble>
          <strong>{ms.sage}</strong><br />
          {ms.body}
        </SageBubble>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <SkipBtn onClick={handleSkip} label={t('onboarding.skipTour', 'Skip tour')} />
          <PrimaryBtn onClick={() => step < 3 ? advanceStep(step + 1) : advanceStep(4)}>
            {ms.cta}
          </PrimaryBtn>
        </div>
      </div>
    )
  }

  return null
}
